import React, { useId, useMemo } from 'react';
import { Icon } from './Icons.jsx';
import { formatValue } from '../lib/format.js';

const WIDTH = 600;
const HEIGHT = 118;
const MOMENTUM_HEIGHT = 44;

function downsample(samples, max = 300) {
  if (samples.length <= max) return samples;
  const step = Math.ceil(samples.length / max);
  const result = [];
  for (let i = 0; i < samples.length; i += step) result.push(samples[i]);
  if (result[result.length - 1] !== samples[samples.length - 1]) result.push(samples[samples.length - 1]);
  return result;
}

function buildPath(samples, accessor, height, min, max, leftPad = 0) {
  if (samples.length === 0) return { line: '', area: '' };
  const tMin = samples[0].t;
  const tMax = Math.max(samples[samples.length - 1].t, tMin + 0.001);
  const x = (t) => leftPad + ((t - tMin) / (tMax - tMin)) * (WIDTH - leftPad);
  const y = (value) => {
    const ratio = (value - min) / Math.max(max - min, 1e-9);
    return height - Math.min(1, Math.max(0, ratio)) * height;
  };
  const points = samples.map((sample) => `${x(sample.t).toFixed(2)},${y(accessor(sample)).toFixed(2)}`);
  const line = `M${points.join('L')}`;
  const area = `${line}L${x(samples[samples.length - 1].t).toFixed(2)},${height}L${x(samples[0].t).toFixed(2)},${height}Z`;
  return { line, area };
}

function EnergyPlot({ samples }) {
  const id = useId().replace(/:/g, '');
  const view = downsample(samples);
  const values = view.flatMap((sample) => [sample.energy, sample.kinetic, sample.potential]);
  const max = Math.max(...values, 1e-6) * 1.12;
  const min = Math.min(0, ...values);
  const total = buildPath(view, (sample) => sample.energy, HEIGHT, min, max);
  const kinetic = buildPath(view, (sample) => sample.kinetic, HEIGHT, min, max);
  const potential = buildPath(view, (sample) => sample.potential, HEIGHT, min, max);
  return (
    <div className="plot">
      <div className="plot-axis">
        <span>{formatValue(max, 2)}</span>
        <span>0</span>
      </div>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} preserveAspectRatio="none" role="img" aria-label="机械能、动能与势能随时间变化">
        <defs>
          <linearGradient id={`energyFill-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((ratio) => (
          <line
            key={ratio}
            x1="0"
            y1={HEIGHT * ratio}
            x2={WIDTH}
            y2={HEIGHT * ratio}
            className="plot-gridline"
          />
        ))}
        <path d={total.area} fill={`url(#energyFill-${id})`} />
        <path d={potential.line} className="plot-line potential" vectorEffect="non-scaling-stroke" />
        <path d={kinetic.line} className="plot-line kinetic" vectorEffect="non-scaling-stroke" />
        <path d={total.line} className="plot-line total" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

function MomentumPlot({ samples }) {
  const view = downsample(samples);
  const values = view.map((sample) => sample.momentum ?? 0);
  const maxAbs = Math.max(1e-6, ...values.map((value) => Math.abs(value))) * 1.15;
  const path = buildPath(view, (sample) => sample.momentum ?? 0, MOMENTUM_HEIGHT, -maxAbs, maxAbs);
  return (
    <div className="momentum-plot">
      <span className="plot-title">Σp</span>
      <svg viewBox={`0 0 ${WIDTH} ${MOMENTUM_HEIGHT}`} preserveAspectRatio="none" role="img" aria-label="总动量随时间变化">
        <line x1="0" y1={MOMENTUM_HEIGHT / 2} x2={WIDTH} y2={MOMENTUM_HEIGHT / 2} className="plot-gridline" />
        <path d={path.line} className="plot-line momentum" vectorEffect="non-scaling-stroke" />
      </svg>
      <span className="plot-zero">0</span>
    </div>
  );
}

export function TelemetryChart({ samples, snapshot, running }) {
  const latest = samples[samples.length - 1];
  const hasMomentum = Boolean(snapshot.metrics.momentum);
  const stats = useMemo(() => {
    if (!latest) return null;
    return {
      energy: latest.energy,
      kinetic: latest.kinetic,
      potential: latest.potential,
      momentum: latest.momentum,
    };
  }, [latest]);

  return (
    <div className="chart-card">
      <div className="chart-head">
        <div className="chart-title">
          <Icon name="chart" size={16} />
          <strong>实时遥测</strong>
          <span className={`chart-live ${running ? 'active' : ''}`}>
            <i />
            {running ? '采集中' : '已暂停'}
          </span>
        </div>
        <div className="chart-legend">
          <span className="legend-total">
            <i />
            E {formatValue(stats?.energy ?? snapshot.metrics.energy.total, 2)} J
          </span>
          <span className="legend-kinetic">
            <i />
            Eₖ
          </span>
          <span className="legend-potential">
            <i />
            Eₚ
          </span>
          {hasMomentum && (
            <span className="legend-momentum">
              <i />
              Σp
            </span>
          )}
        </div>
      </div>

      {samples.length > 1 ? (
        <>
          <EnergyPlot samples={samples} />
          {hasMomentum && <MomentumPlot samples={samples} />}
        </>
      ) : (
        <div className="chart-empty">
          <div className="empty-wave" />
          <span>点击播放，开始记录守恒量时间序列</span>
        </div>
      )}

      <div className="chart-foot">
        <span>窗口 {samples.length > 1 ? `${samples[samples.length - 1].t.toFixed(1)} s` : '0.0 s'}</span>
        <span>采样 10 Hz · 相对误差自动比对基线</span>
        <span className="chart-threshold">
          告警阈值 {(((snapshot.conservation.energy?.tolerance ?? 0.002) * 100).toFixed(2))}%
        </span>
      </div>
    </div>
  );
}
